import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	registerRemixCancellationSession,
	type RemixCancellationDiagnostic,
	unregisterRemixCancellationSession,
} from '@atlassian/conversation-assistant-store/controllers/remix-cancellation-signals';
import {
	endRemix,
	sendRemixEvent,
	startRemix,
	type RemixPhase,
	useMachinePhase,
} from '@atlassian/malleable-ui-components/state/machine';
import type { RemixExplicitFlowEndReason } from '@atlassian/malleable-ui-components/state/machine/remix-command-bus';

type PendingIntent = 'preview-opened' | undefined;
type AdmissionRejectionReason = 'active-flow' | 'error' | 'identity-mismatch';
type LifecycleOperation =
	| 'endRemix'
	| 'notifyAdmissionRejected'
	| 'sendPreviewOpened'
	| 'sendTerminalEvent'
	| 'sendUnmountCancelled'
	| 'startRemix'
	| 'validateAdmissionIdentity';

type StartRendererRemixArgs = {
	conversationId?: string;
	dynamicUiType: string;
	dynamicUiSubtype?: string;
	source?: InteractionSource;
};

type RemixAdmission =
	| { conversationId: string; admitted: true; cancellationSessionId: string }
	| { conversationId: string; admitted: false; reason: AdmissionRejectionReason };

export type RendererRemixMachineLifecycle = {
	phase: RemixPhase | undefined;
	/** Identifies the admitted flow so parity diagnostics can scope themselves to it. */
	remixId: string | undefined;
	/** Starts authoritative machine admission before the renderer sends its Rovo request. */
	startFlow: (args: StartRendererRemixArgs) => RemixAdmission;
	onPreviewOpened: () => void;
	onPreviewDismissed: () => void;
	onStopThinking: () => void;
	onRovoAborted: () => void;
	onErrorDismissed: () => void;
	onSendRejected: () => void;
	onNavigation: () => void;
};

/** Adapts renderer UI intent to the Remix state machine. */
export const useRendererRemixMachineLifecycle = ({
	onAdmissionRejected,
}: {
	onAdmissionRejected?: (reason: AdmissionRejectionReason) => void;
} = {}): RendererRemixMachineLifecycle => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const logLifecycleOperationError = useCallback(
		(operation: LifecycleOperation, error: unknown) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'rendererRemixLifecycleOperation',
					source: 'rendererRemix',
					attributes: {
						operation,
						errorName: error instanceof Error ? error.name : typeof error,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
	const reportCancellationDiagnostic = useCallback(
		(diagnostic: RemixCancellationDiagnostic) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'observed',
					actionSubject: 'rendererRemixCancellationShadow',
					source: 'rendererRemix',
					attributes: {
						diagnosticKind: diagnostic.kind,
						machinePhase: diagnostic.phase,
						cancellationScope: diagnostic.scope,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
	const runLifecycleSafely = useCallback(
		(operationName: LifecycleOperation, operation: () => void): boolean => {
			try {
				operation();
				return true;
			} catch (error) {
				logLifecycleOperationError(operationName, error);
				return false;
			}
		},
		[logLifecycleOperationError],
	);

	const [remixId, setRemixId] = useState<string>();
	const phase = useMachinePhase(remixId ?? '');
	const phaseRef = useRef(phase);
	phaseRef.current = phase;
	const remixIdRef = useRef<string | undefined>(undefined);
	const cancellationSessionIdRef = useRef<string | undefined>(undefined);
	const pendingIntentRef = useRef<PendingIntent>(undefined);
	const finalizedRef = useRef(true);

	const releaseLocalFlow = useCallback(() => {
		const cancellationSessionId = cancellationSessionIdRef.current;
		cancellationSessionIdRef.current = undefined;
		finalizedRef.current = true;
		remixIdRef.current = undefined;
		pendingIntentRef.current = undefined;
		if (cancellationSessionId) {
			unregisterRemixCancellationSession(cancellationSessionId);
		}
	}, []);
	const safelyEndRemix = useCallback(
		(id: string, reason: RemixExplicitFlowEndReason) => {
			runLifecycleSafely('endRemix', () => endRemix(id, reason));
		},
		[runLifecycleSafely],
	);
	const endObservedTerminal = useCallback(
		(reason: RemixExplicitFlowEndReason) => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}
			releaseLocalFlow();
			safelyEndRemix(activeRemixId, reason);
		},
		[releaseLocalFlow, safelyEndRemix],
	);

	const endWith = useCallback(
		({
			event,
			reason,
		}: {
			event: 'GENERATION_ABORTED' | 'GENERATION_ERROR' | 'CANCELLED';
			reason: RemixExplicitFlowEndReason;
		}) => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}

			// Clear first so re-entrant modal callbacks cannot finalize twice.
			releaseLocalFlow();
			runLifecycleSafely('sendTerminalEvent', () => sendRemixEvent(activeRemixId, { type: event }));
			safelyEndRemix(activeRemixId, reason);
		},
		[releaseLocalFlow, runLifecycleSafely, safelyEndRemix],
	);

	const startFlow = useCallback(
		({
			conversationId: requestedConversationId,
			dynamicUiType,
			dynamicUiSubtype,
			source,
		}: StartRendererRemixArgs): RemixAdmission => {
			const conversationId = requestedConversationId ?? uuidv4();

			// Preserve any existing admitted flow. SINGLE_ACTIVE_POLICY owns rejection.
			if (remixIdRef.current && !finalizedRef.current) {
				runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
				return { conversationId, admitted: false, reason: 'active-flow' };
			}

			const cancellationSessionId = registerRemixCancellationSession({
				conversationId,
				reportDiagnostic: reportCancellationDiagnostic,
			});
			if (!cancellationSessionId) {
				runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
				return { conversationId, admitted: false, reason: 'active-flow' };
			}

			try {
				const result = startRemix({
					kind: 'create',
					conversationId,
					cancellationSessionId,
					metadata: { dynamicUiType, dynamicUiSubtype, source },
				});

				if (!result) {
					unregisterRemixCancellationSession(cancellationSessionId);
					runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('active-flow'));
					return { conversationId, admitted: false, reason: 'active-flow' };
				}

				if (result.conversationId !== conversationId) {
					unregisterRemixCancellationSession(cancellationSessionId);
					logLifecycleOperationError(
						'validateAdmissionIdentity',
						new Error('Machine admission returned a different conversation identity'),
					);
					runLifecycleSafely('notifyAdmissionRejected', () =>
						onAdmissionRejected?.('identity-mismatch'),
					);
					safelyEndRemix(result.remixId, 'cancelled');
					return { conversationId, admitted: false, reason: 'identity-mismatch' };
				}

				finalizedRef.current = false;
				remixIdRef.current = result.remixId;
				cancellationSessionIdRef.current = cancellationSessionId;
				pendingIntentRef.current = undefined;
				setRemixId(result.remixId);
				return { conversationId, admitted: true, cancellationSessionId };
			} catch (error) {
				unregisterRemixCancellationSession(cancellationSessionId);
				logLifecycleOperationError('startRemix', error);
				runLifecycleSafely('notifyAdmissionRejected', () => onAdmissionRejected?.('error'));
				return { conversationId, admitted: false, reason: 'error' };
			}
		},
		[
			logLifecycleOperationError,
			onAdmissionRejected,
			reportCancellationDiagnostic,
			runLifecycleSafely,
			safelyEndRemix,
		],
	);

	const reconcileIntent = useCallback(
		(intent: Exclude<PendingIntent, undefined>) => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}

			const currentPhase = phaseRef.current;
			if (intent === 'preview-opened' && currentPhase === 'ready') {
				pendingIntentRef.current = undefined;
				if (
					!runLifecycleSafely('sendPreviewOpened', () =>
						sendRemixEvent(activeRemixId, { type: 'PREVIEW_OPENED' }),
					)
				) {
					releaseLocalFlow();
					safelyEndRemix(activeRemixId, 'cancelled');
				}
				return;
			}
		},
		[releaseLocalFlow, runLifecycleSafely, safelyEndRemix],
	);

	const onPreviewOpened = useCallback(() => {
		if (remixIdRef.current && !finalizedRef.current) {
			pendingIntentRef.current = 'preview-opened';
			reconcileIntent('preview-opened');
		}
	}, [reconcileIntent]);

	const onPreviewDismissed = useCallback(() => {
		endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	const onStopThinking = useCallback(() => {
		endWith({ event: 'GENERATION_ABORTED', reason: 'aborted' });
	}, [endWith]);

	const onRovoAborted = useCallback(() => {
		endObservedTerminal('aborted');
	}, [endObservedTerminal]);

	const onErrorDismissed = useCallback(() => {
		endObservedTerminal('cancelled');
	}, [endObservedTerminal]);

	const onSendRejected = useCallback(() => {
		endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	const onNavigation = useCallback(() => {
		endWith({ event: 'CANCELLED', reason: 'cancelled' });
	}, [endWith]);

	useEffect(() => {
		const pendingIntent = pendingIntentRef.current;
		if (!pendingIntent) {
			return;
		}

		// Reconcile the queued renderer intent only after the watcher exposes a legal machine phase.
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		reconcileIntent(pendingIntent);
	}, [phase, reconcileIntent]);

	// Component teardown mirrors legacy dismissal and releases machine admission.
	useEffect(() => {
		return () => {
			const activeRemixId = remixIdRef.current;
			if (!activeRemixId || finalizedRef.current) {
				return;
			}
			releaseLocalFlow();
			runLifecycleSafely('sendUnmountCancelled', () =>
				sendRemixEvent(activeRemixId, { type: 'CANCELLED' }),
			);
			safelyEndRemix(activeRemixId, 'cancelled');
		};
	}, [releaseLocalFlow, runLifecycleSafely, safelyEndRemix]);

	return {
		phase,
		remixId,
		startFlow,
		onPreviewOpened,
		onPreviewDismissed,
		onStopThinking,
		onRovoAborted,
		onErrorDismissed,
		onSendRejected,
		onNavigation,
	};
};
